import {Alert, Button, Checkbox, Form, Input} from 'antd';
import {Formik} from 'formik';
import React, {useEffect, useState} from 'react';
import * as Yup from 'yup';

import {ClientData, SignupRequestParams} from '../../../../types/src';
import {FormRow, RequiredLabel} from '../formComponents';
import {useErrorHandler} from '../../../../../utils/hooks';
import {brandConfig, getCookie, STRINGS} from '../../utils';
import {trackRegistration} from '../../utils/functions/trackRegistration';

import './registerForm.scss';

interface RegisterStep1Props {
  client: ClientData;
  signup: (params: SignupRequestParams) => Promise<boolean>;
  goToStep2: () => void;
  analyticsService: any;
}

const RegisterStep1Schema = Yup.object().shape({
  name: Yup.string().required('Required'),
  password: Yup.string().required('Required'),
  email: Yup.string()
    .test('email', 'Invalid email format', value => {
      const emailRegex =
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return emailRegex.test(value);
    })
    .required('Required'),
});

const initialValues: SignupRequestParams = {
  name: '',
  email: '',
  password: '',
  attributed: false,
  source: '',
};

export const RegisterStep1 = ({client: {status, loading}, signup, goToStep2, analyticsService}: RegisterStep1Props) => {
  const [termsChecked, setTermsChecked] = useState(brandConfig.isAffiliate);
  const {handleError, clearError} = useErrorHandler();
  const [initialFormValues, setInitialFormValues] = useState(initialValues);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');

    if (emailParam) {
      setInitialFormValues({
        ...initialValues,
        email: decodeURIComponent(emailParam),
      });
    }
  }, []);

  const handleSubmit = async (form: SignupRequestParams) => {
    const utm_campaign = getCookie('utm_campaign');
    const isCreated = await signup({
      name: form.name,
      email: form.email,
      password: form.password,
      attributed: Boolean(utm_campaign),
      source: utm_campaign,
    });

    clearError();

    if (isCreated) {
      try {
        trackRegistration({
          analyticsService,
          email: form.email,
          name: form.name,
        });

        goToStep2();
      } catch (err) {
        handleError(err);
      }
    }
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={RegisterStep1Schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({values, handleSubmit, setFieldValue, errors, touched}) => (
        <Form className="registerForm" onFinish={handleSubmit} layout="vertical">
          <FormRow>
            <Form.Item
              label={<RequiredLabel label="Name" />}
              colon={false}
              validateStatus={errors.name && touched.name ? 'error' : ''}
              hasFeedback
              help={errors.name && touched.name ? errors.name : ''}
            >
              <Input
                value={values.name}
                onChange={e => setFieldValue('name', e.target.value)}
                placeholder="Enter your name"
              />
            </Form.Item>
          </FormRow>
          <FormRow>
            <Form.Item
              label={<RequiredLabel label="Email" />}
              colon={false}
              validateStatus={errors.email && touched.email ? 'error' : ''}
              hasFeedback
              help={errors.email && touched.email ? errors.email : ''}
            >
              <Input
                type="text"
                placeholder="Enter your email"
                autoComplete="off"
                value={values.email}
                onChange={e => setFieldValue('email', e.target.value.replace(/\s/g, ''))}
              />
            </Form.Item>
          </FormRow>
          <FormRow>
            <Form.Item
              label={<RequiredLabel label="Create password" />}
              colon={false}
              validateStatus={errors.password && touched.password ? 'error' : ''}
              hasFeedback
              help={errors.password && touched.password ? errors.password : ''}
            >
              <Input.Password
                autoComplete="off"
                placeholder="Enter your password"
                value={values.password}
                onChange={e => setFieldValue('password', e.target.value)}
              />
            </Form.Item>
          </FormRow>
          {!brandConfig.isAffiliate && (
            <Form.Item>
              <Checkbox checked={termsChecked} onChange={e => setTermsChecked(e.target.checked)}>
                By clicking you agree to our{' '}
                <a href={STRINGS.url.terms} target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>{' '}
                and{' '}
                <a href={STRINGS.url.privacy} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </Checkbox>
            </Form.Item>
          )}
          {!!status.length && (
            <Alert message="Sign up failed" description={status} type="error" style={{marginBottom: '10px'}} />
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              disabled={!termsChecked && !brandConfig.isAffiliate}
              style={{marginTop: '20px'}}
            >
              Sign up
            </Button>
          </Form.Item>
        </Form>
      )}
    </Formik>
  );
};
